/**
 * @fileoverview Data model representing a single Task, for creating elements of a UX Taskflow.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */
import {EnterpriseConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/enterpriseconfiguration';
import {PageControls} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/hooks/usenavigation';

export class Task {
  /**
   * @param {{
   *   id: string,
   *   getPage: GetPageCallback,
   *   getCard: GetCardCallback,
   *   isComplete: IsCompleteCallback,
   *   isSectionSkipped: IsSectionSkippedCallback,
   * }} params
   */
  constructor(params) {
    /** @type {string} */
    this.id = params.id;
    /** @type {GetPageCallback} */
    this.getPage = params.getPage;
    /** @type {GetCardCallback} */
    this.getCard = params.getCard;
    /** @type {IsCompleteCallback} */
    this.isComplete = params.isComplete;
    /** @type {IsSectionSkippedCallback} */
    this.isSectionSkipped = params.isSectionSkipped;
  }
}

/**
 * The `getPage` callback accepts a configuration, a setter callback for the configuration, and a
 * set of callbacks that can be used to control navigation. A Taskflow.Types.PageType is returned,
 * to be used with the UX Taskflow `pages` property.
 * @typedef {function(EnterpriseConfiguration, function(EnterpriseConfiguration): void, PageControls): ?}
 */
let GetPageCallback;

/**
 * The `getCard` callback accepts a configuration, the step index (for ordered list numbering), the
 * currently selected Page ID, a setter callback for the Page ID, and a boolean for disabling the
 * card click event. A Taskflow.Types.Card is returned, to be used with the UX Taskflow `sections`.
 * @typedef {function(EnterpriseConfiguration, number, string, function(string): void, boolean): ?}
 */
let GetCardCallback;

/**
 * The `isComplete` callback accepts a configuration, and returns true if the fields relevant to the
 * task are filled out and ready for submission. Note that this callback should return false if the
 * task if in a skipped state; this logic is handled separately at the taskflow level.
 * @typedef {function(EnterpriseConfiguration): boolean}
 */
let IsCompleteCallback;

/**
 * The `IsSectionSkippedCallback` callback accepts a configuration, and returns true if the current 
 * section is skipped.
 * @typedef {function(EnterpriseConfiguration): boolean}
 */
let IsSectionSkippedCallback;
